import React, { useState, useEffect } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import { useQuery, useMutation, gql } from "@apollo/client";
import { Link } from "react-router-dom";
import { API_URL } from "../config";
import { useCurrentUser } from "../context/AuthContext";
import { useAcknowledgedTerms } from "../hooks/useAcknowledgedTerms";
import StatusBadge from "../components/StatusBadge";
import Stars from "../components/Stars";
import { blendedRating } from "../utils/reviewScore";
import ProfileCard from "../components/ProfileCard";
import ProfileForm, { ProfileFields } from "../components/ProfileForm";
import { nameFor } from "../utils/displayName";

async function bookingAction(path: string): Promise<void> {
  const token = sessionStorage.getItem("jwt");
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error ?? "Request failed");
  }
}

function useIsNarrow() {
  const [narrow, setNarrow] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return narrow;
}

const CREATE_ACKNOWLEDGED_TERMS = gql`
  mutation DashCreateAcknowledgedTerms($userId: Int!) {
    createAcknowledgedTerm(input: {
      acknowledgedTerm: { userId: $userId, isHost: true, isGuest: true }
    }) {
      acknowledgedTerm { id }
    }
  }
`;

// ── Queries ────────────────────────────────────────────────────────────────

const MY_SAUNAS = gql`
  query MySaunas($userId: Int!) {
    allSaunas(condition: { ownerId: $userId }, orderBy: CREATED_AT_DESC) {
      nodes {
        id name location pricePerHourCents isActive
        bookingsBySaunaId(orderBy: START_AT_ASC) {
          nodes {
            id startAt endAt status totalCents grandTotalCents hostPayoutCents
            userByRenterId {
              userProfileByUserId { fullName displayName }
            }
          }
        }
        reviewsBySaunaId(condition: { reviewerRole: "guest" }, orderBy: CREATED_AT_DESC) {
          nodes {
            id overall cleanliness communication accuracy valueScore body createdAt
            userByReviewerId { userProfileByUserId { fullName displayName } }
          }
        }
      }
    }
  }
`;

const MY_RENTER_BOOKINGS = gql`
  query MyRenterBookings($userId: Int!) {
    allBookings(condition: { renterId: $userId }, orderBy: ID_DESC) {
      nodes {
        id startAt endAt status totalCents grandTotalCents
        saunaBySaunaId { name location }
      }
    }
  }
`;

const MY_WRITTEN_REVIEWS = gql`
  query MyWrittenReviews($userId: Int!) {
    allReviews(condition: { reviewerId: $userId }, orderBy: CREATED_AT_DESC) {
      nodes {
        id overall reviewerRole cleanliness communication accuracy valueScore respectRules body createdAt
        saunaBySaunaId { name }
      }
    }
  }
`;

const MY_PENDING_REVIEWS = gql`
  query MyPendingReviews {
    myPendingReviews {
      nodes {
        bookingId saunaId saunaName reviewerRole endAt
      }
    }
  }
`;

const TOGGLE_SAUNA = gql`
  mutation ToggleSauna($id: Int!, $isActive: Boolean!) {
    setSaunaActive(input: { id: $id, isActive: $isActive }) {
      sauna { id isActive }
    }
  }
`;


const GET_PROFILE = gql`
  query GetUserProfile($userId: Int!) {
    userById(id: $userId) {
      createdAt
      userProfileByUserId {
        id
        fullName
        displayName
        hometown
        school
        work
        interests
        bio
        preferredSaunaTempF
      }
    }
  }
`;

const CREATE_PROFILE = gql`
  mutation CreateUserProfile($userId: Int!, $fullName: String, $displayName: String, $hometown: String, $school: String, $work: String, $interests: String, $bio: String, $preferredSaunaTempF: Int) {
    createUserProfile(input: {
      userProfile: {
        userId: $userId
        fullName: $fullName
        displayName: $displayName
        hometown: $hometown
        school: $school
        work: $work
        interests: $interests
        bio: $bio
        preferredSaunaTempF: $preferredSaunaTempF
      }
    }) {
      userProfile { id }
    }
  }
`;

const UPDATE_PROFILE = gql`
  mutation UpdateUserProfile($userId: Int!, $fullName: String, $displayName: String, $hometown: String, $school: String, $work: String, $interests: String, $bio: String, $preferredSaunaTempF: Int) {
    updateUserProfileByUserId(input: {
      userId: $userId
      userProfilePatch: {
        fullName: $fullName
        displayName: $displayName
        hometown: $hometown
        school: $school
        work: $work
        interests: $interests
        bio: $bio
        preferredSaunaTempF: $preferredSaunaTempF
      }
    }) {
      userProfile { id }
    }
  }
`;

// ── Helpers ─────────────────────────────────────────────────────────────────

const now = new Date();

function isUpcoming(dateStr: string) {
  return new Date(dateStr) >= now;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function formatPrice(cents: number) {
  return `$${(cents / 100).toFixed(0)}/hr`;
}

// ── Sections ─────────────────────────────────────────────────────────────────

function TermsBanner({ userId }: { userId: number }) {
  const { acknowledged, loading, refetch } = useAcknowledgedTerms(userId);
  const [createAck] = useMutation(CREATE_ACKNOWLEDGED_TERMS);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading || acknowledged) return null;

  const handleAccept = async () => {
    if (!agreed) return;
    setSaving(true);
    setError(null);
    try {
      await createAck({ variables: { userId } });
      await refetch();
    } catch (err: any) {
      setError(err?.message ?? "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={termsBannerStyle}>
      <p style={{ margin: "0 0 6px", fontWeight: 600, fontSize: 15, color: "#92400e" }}>
        Action required — please accept our terms before booking or listing
      </p>
      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
        Saunas involve heat, water, and inherent physical risk. By accepting you acknowledge that
        accidents, serious injury, or even death may occur. This application accepts complete and
        utter zero responsibility for any harm, illness, or property damage arising from sauna
        use, whether as a host or a guest. You agree to use this platform lawfully and to take
        full responsibility for your own safety and the safety of anyone accompanying you. Hosts
        are solely responsible for maintaining a safe environment on their property. Lets Go Sauna acts as
        a marketplace only and makes no warranties regarding any listing, user, or sauna condition.
      </p>
      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={agreed}
          onChange={e => setAgreed(e.target.checked)}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <span>I acknowledge and accept these terms.</span>
      </label>
      {error && <p style={{ fontSize: 13, color: "#ef4444", margin: "0 0 8px" }}>{error}</p>}
      <button
        onClick={handleAccept}
        disabled={!agreed || saving}
        style={{
          padding: "9px 22px", background: agreed ? "#92400e" : "#d1d5db",
          color: "#fff", border: "none", borderRadius: 6,
          cursor: agreed ? "pointer" : "not-allowed", fontSize: 14, fontWeight: 600,
        }}
      >
        {saving ? "Saving..." : "Accept and continue"}
      </button>
    </div>
  );
}

function PendingReviewsNudge() {
  const { data, loading } = useQuery(MY_PENDING_REVIEWS, { fetchPolicy: "network-only" });
  if (loading) return null;
  const pending = [...(data?.myPendingReviews?.nodes ?? [])].sort(
    (a: any, b: any) => new Date(b.endAt).getTime() - new Date(a.endAt).getTime()
  );
  if (pending.length === 0) return null;

  return (
    <Section title={`Reviews to leave (${pending.length})`}>
      {pending.map((item: any) => (
        <Card key={`${item.bookingId}-${item.reviewerRole}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{item.saunaName}</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                Stayed {formatDate(item.endAt)} · as {item.reviewerRole}
              </div>
            </div>
            <Link
              to={`/bookings/${item.bookingId}/review`}
              style={{ fontSize: 13, color: "#fff", background: "#2563eb", padding: "7px 16px", borderRadius: 6, textDecoration: "none", fontWeight: 600, whiteSpace: "nowrap" }}
            >
              Leave review
            </Link>
          </div>
        </Card>
      ))}
    </Section>
  );
}

function PendingRequests({ userId }: { userId: number }) {
  const { data, loading, refetch } = useQuery(MY_SAUNAS, { variables: { userId }, fetchPolicy: "network-only" });

  if (loading) return null;

  const allBookings = (data?.allSaunas?.nodes ?? []).flatMap((s: any) =>
    (s.bookingsBySaunaId?.nodes ?? []).map((b: any) => ({ ...b, saunaName: s.name }))
  );

  const actionRequired = allBookings.filter((b: any) => b.status === "pending_approval");
  const awaitingPayment = allBookings.filter((b: any) => b.status === "pending_payment");

  if (actionRequired.length === 0 && awaitingPayment.length === 0) return null;

  return (
    <div style={pendingBannerStyle}>
      {actionRequired.length > 0 && (
        <>
          <p style={{ margin: "0 0 12px", fontWeight: 700, fontSize: 15, color: "#92400e" }}>
            Action required — {actionRequired.length} booking request{actionRequired.length !== 1 ? "s" : ""} need{actionRequired.length === 1 ? "s" : ""} your approval
          </p>
          {actionRequired.map((b: any) => {
            const guest = b.userByRenterId;
            const guestName = nameFor(guest?.userProfileByUserId, "Guest");
            return (
              <div key={b.id} style={pendingRowStyle}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{b.saunaName}</div>
                  <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                    {formatDate(b.startAt)} → {formatDate(b.endAt)}
                  </div>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>Guest: {guestName}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, marginTop: 4 }}>
                    You receive: ${((b.hostPayoutCents ?? b.totalCents) / 100).toFixed(2)}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0, alignItems: "flex-end" }}>
                  <button
                    onClick={async () => {
                      try {
                        await bookingAction(`/api/bookings/${b.id}/approve`);
                        await refetch();
                      } catch (err: any) { alert(err?.message ?? "Failed to approve"); }
                    }}
                    style={approveBtn}
                  >
                    Approve
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Decline this request?")) return;
                      try {
                        await bookingAction(`/api/bookings/${b.id}/decline`);
                        await refetch();
                      } catch (err: any) { alert(err?.message ?? "Failed to decline"); }
                    }}
                    style={declineBtn}
                  >
                    Decline
                  </button>
                  <Link to={`/bookings/${b.id}`} style={{ fontSize: 12, color: "#2563EB", textDecoration: "none" }}>
                    Details
                  </Link>
                </div>
              </div>
            );
          })}
        </>
      )}

      {awaitingPayment.length > 0 && (
        <>
          <p style={{ margin: `${actionRequired.length > 0 ? "20px" : "0"} 0 10px`, fontWeight: 600, fontSize: 14, color: "#78350f" }}>
            Awaiting guest payment — {awaitingPayment.length}
          </p>
          {awaitingPayment.map((b: any) => {
            const guest = b.userByRenterId;
            const guestName = nameFor(guest?.userProfileByUserId, "Guest");
            return (
              <div key={b.id} style={{ ...pendingRowStyle, borderColor: "#fde68a" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{b.saunaName}</div>
                  <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
                    {formatDate(b.startAt)} → {formatDate(b.endAt)}
                  </div>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>Guest: {guestName}</div>
                </div>
                <Link to={`/bookings/${b.id}`} style={{ fontSize: 12, color: "#2563EB", textDecoration: "none", flexShrink: 0 }}>
                  Details
                </Link>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function MySaunas({ userId }: { userId: number }) {
  const { data, loading } = useQuery(MY_SAUNAS, { variables: { userId } });
  const [toggleSauna] = useMutation(TOGGLE_SAUNA, { refetchQueries: ["MySaunas"] });

  if (loading) return <p style={emptyStyle}>Loading...</p>;
  const saunas = data?.allSaunas?.nodes ?? [];

  return (
    <Section title="My Saunas" action={<Link to="/saunas/new" style={actionLink}>+ Add Sauna</Link>}>
      {saunas.length === 0 ? (
        <p style={emptyStyle}>No saunas yet. <Link to="/saunas/new">List your first sauna →</Link></p>
      ) : saunas.map((s: any) => (
        <Card key={s.id}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <strong>{s.name}</strong>
              {s.location && <span style={{ color: "#6b7280", marginLeft: 8, fontSize: 13 }}>{s.location}</span>}
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>{formatPrice(s.pricePerHourCents)}</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Link to={`/saunas/${s.id}/edit`} style={{ fontSize: 12, color: "#2563EB", textDecoration: "none" }}>Edit</Link>
              <button
                onClick={() => toggleSauna({ variables: { id: s.id, isActive: !s.isActive } })}
                style={{ ...toggleBtn, background: s.isActive ? "#16a34a" : "#9ca3af" }}
              >
                {s.isActive ? "Active" : "Inactive"}
              </button>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
}

function UpcomingBookings({ userId }: { userId: number }) {
  const { data: saunaData, loading: l1 } = useQuery(MY_SAUNAS, { variables: { userId } });
  const { data: renterData, loading: l2 } = useQuery(MY_RENTER_BOOKINGS, { variables: { userId } });

  const hostBookings = (saunaData?.allSaunas?.nodes ?? [])
    .flatMap((s: any) =>
      (s.bookingsBySaunaId?.nodes ?? [])
        .filter((b: any) => isUpcoming(b.startAt))
        .map((b: any) => ({ ...b, saunaName: s.name }))
    )
    .sort((a: any, b: any) => b.id - a.id);

  const renterBookings = (renterData?.allBookings?.nodes ?? []).filter((b: any) => isUpcoming(b.startAt));

  if (l1 || l2) return <p style={emptyStyle}>Loading...</p>;

  return (
    <Section title="Upcoming Bookings">
      <SubHeading>As a host</SubHeading>
      {hostBookings.length === 0 ? (
        <p style={emptyStyle}>No upcoming guest bookings.</p>
      ) : hostBookings.map((b: any) => (
        <Card key={b.id}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <Link to={`/bookings/${b.id}`} style={{ fontWeight: 600, color: "#111827", textDecoration: "none" }}>
                {b.saunaName}
              </Link>
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                {formatDate(b.startAt)} → {formatDate(b.endAt)}
              </div>
              <div style={{ fontSize: 13, color: "#6b7280" }}>Guest: {nameFor(b.userByRenterId?.userProfileByUserId, "Guest")}</div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              <StatusBadge status={b.status} />
              <span style={{ fontSize: 13 }}>You receive: ${((b.hostPayoutCents ?? b.totalCents) / 100).toFixed(2)}</span>
            </div>
          </div>
        </Card>
      ))}

      <SubHeading style={{ marginTop: 20 }}>As a renter</SubHeading>
      {renterBookings.length === 0 ? (
        <p style={emptyStyle}>No upcoming bookings. <Link to="/listings">Browse saunas →</Link></p>
      ) : renterBookings.map((b: any) => (
        <Card key={b.id}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <Link to={`/bookings/${b.id}`} style={{ fontWeight: 600, color: "#111827", textDecoration: "none" }}>
                {b.saunaBySaunaId?.name}
              </Link>
              {b.saunaBySaunaId?.location && (
                <span style={{ color: "#6b7280", marginLeft: 8, fontSize: 13 }}>{b.saunaBySaunaId.location}</span>
              )}
              <div style={{ fontSize: 13, color: "#6b7280" }}>
                {formatDate(b.startAt)} → {formatDate(b.endAt)}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              <StatusBadge status={b.status} />
              <span style={{ fontSize: 13 }}>You paid: ${((b.grandTotalCents ?? b.totalCents) / 100).toFixed(2)}</span>
            </div>
          </div>
        </Card>
      ))}
    </Section>
  );
}

function CriteriaLine({ r }: { r: any }) {
  const role = r.reviewerRole as "guest" | "host";
  const parts = [
    r.cleanliness   != null && `Cleanliness ${r.cleanliness}/5`,
    r.communication != null && `Communication ${r.communication}/5`,
    role === "guest" && r.accuracy   != null && `Accuracy ${r.accuracy}/5`,
    role === "guest" && r.valueScore != null && `Value ${r.valueScore}/5`,
    role === "host"  && r.respectRules != null && `House rules ${r.respectRules}/5`,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{parts.join(" · ")}</div>;
}

function Reviews({ userId }: { userId: number }) {
  const { data: saunaData, loading: l1 } = useQuery(MY_SAUNAS, { variables: { userId } });
  const { data: writtenData, loading: l2 } = useQuery(MY_WRITTEN_REVIEWS, { variables: { userId } });

  const received = (saunaData?.allSaunas?.nodes ?? [])
    .flatMap((s: any) =>
      (s.reviewsBySaunaId?.nodes ?? []).map((r: any) => ({ ...r, saunaName: s.name }))
    )
    .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const written = writtenData?.allReviews?.nodes ?? [];

  if (l1 || l2) return <p style={emptyStyle}>Loading...</p>;

  return (
    <Section title="Reviews">
      <SubHeading>Received on your saunas</SubHeading>
      {received.length === 0 ? (
        <p style={emptyStyle}>No reviews received yet.</p>
      ) : received.map((r: any) => (
        <Card key={r.id}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Stars rating={blendedRating(r)} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>{blendedRating(r).toFixed(1)}/5</span>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{r.saunaName}</span>
          </div>
          <CriteriaLine r={r} />
          {r.body && <p style={{ margin: "4px 0 0", fontSize: 14 }}>{r.body}</p>}
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>
            {nameFor(r.userByReviewerId?.userProfileByUserId, "Guest")} · {formatDate(r.createdAt)}
          </div>
        </Card>
      ))}

      <SubHeading style={{ marginTop: 20 }}>Written by you</SubHeading>
      {written.length === 0 ? (
        <p style={emptyStyle}>You haven't written any reviews yet.</p>
      ) : written.map((r: any) => (
        <Card key={r.id}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Stars rating={blendedRating(r)} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>{blendedRating(r).toFixed(1)}/5</span>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{r.saunaBySaunaId?.name}</span>
          </div>
          <CriteriaLine r={r} />
          {r.body && <p style={{ margin: "4px 0 0", fontSize: 14 }}>{r.body}</p>}
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>{formatDate(r.createdAt)}</div>
        </Card>
      ))}
    </Section>
  );
}

// ── Profile section ──────────────────────────────────────────────────────────

function ProfileSection({ userId }: { userId: number }) {
  const { data } = useQuery(GET_PROFILE, { variables: { userId }, fetchPolicy: "network-only" });
  const [createProfile] = useMutation(CREATE_PROFILE, { refetchQueries: ["GetUserProfile", "NavMe"] });
  const [updateProfile] = useMutation(UPDATE_PROFILE, { refetchQueries: ["GetUserProfile", "NavMe"] });

  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState<ProfileFields>({
    fullName: "", displayName: "", hometown: "", school: "", work: "", interests: "", bio: "", preferredSaunaTempF: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const p = data?.userById?.userProfileByUserId;
    if (p) {
      setFields({
        fullName: p.fullName ?? "",
        displayName: p.displayName ?? "",
        hometown: p.hometown ?? "",
        school: p.school ?? "",
        work: p.work ?? "",
        interests: p.interests ?? "",
        bio: p.bio ?? "",
        preferredSaunaTempF: p.preferredSaunaTempF != null ? String(p.preferredSaunaTempF) : "",
      });
    }
  }, [data]);

  const handleCancel = () => {
    const p = data?.userById?.userProfileByUserId;
    setFields({
      fullName: p?.fullName ?? "",
      displayName: p?.displayName ?? "",
      hometown: p?.hometown ?? "",
      school: p?.school ?? "",
      work: p?.work ?? "",
      interests: p?.interests ?? "",
      bio: p?.bio ?? "",
      preferredSaunaTempF: p?.preferredSaunaTempF != null ? String(p.preferredSaunaTempF) : "",
    });
    setEditing(false);
    setError(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    const exists = !!data?.userById?.userProfileByUserId;
    try {
      const mutation = exists ? updateProfile : createProfile;
      const { errors } = await mutation({
        variables: {
          userId,
          ...fields,
          preferredSaunaTempF: fields.preferredSaunaTempF ? parseInt(fields.preferredSaunaTempF, 10) : null,
        },
      });
      if (errors) { setError(errors[0]?.message ?? "Failed to save"); return; }
      setEditing(false);
    } catch (err: any) {
      setError(err?.message ?? "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const editBtn = (
    <button onClick={() => { setEditing(true); setError(null); }} style={profileEditBtn}>
      {data?.userById?.userProfileByUserId ? "Edit" : "Add details"}
    </button>
  );

  if (!editing) {
    return (
      <div style={{ marginBottom: 32 }}>
        <ProfileCard
          user={data?.userById ?? {}}
          heading="Your profile"
          sinceLabel="Member"
          headerRight={editBtn}
        />
        {!data?.userById?.userProfileByUserId && (
          <p style={{ fontSize: 13, color: "#9ca3af", margin: "8px 0 0 4px" }}>
            Add details so hosts know who's booking.
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 20, background: "#fff", marginBottom: 32 }}>
      <h4 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 600 }}>Your profile</h4>
      {error && <p style={{ color: "#ef4444", fontSize: 14, margin: "0 0 12px" }}>{error}</p>}
      <form onSubmit={handleSave}>
        <ProfileForm fields={fields} onChange={setFields} />
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button type="submit" disabled={saving} style={profileSaveBtn}>
            {saving ? "Saving..." : "Save changes"}
          </button>
          <button type="button" onClick={handleCancel} style={profileCancelBtn}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Layout primitives ────────────────────────────────────────────────────────

function Section({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 18 }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function SubHeading({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <h4 style={{ margin: "0 0 8px", fontSize: 13, color: "#6b7280", textTransform: "uppercase", letterSpacing: 1, ...style }}>{children}</h4>;
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px", marginBottom: 10, background: "#fff" }}>
      {children}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function Dashboard() {
  usePageTitle("Dashboard — Lets Go Sauna");
  const userId = useCurrentUser();
  const narrow = useIsNarrow();

  return (
    <div style={{ maxWidth: 1200, margin: "40px auto", padding: "0 24px" }}>
      <h2 style={{ marginBottom: 32 }}>Dashboard</h2>
      <TermsBanner userId={userId} />
      <PendingRequests userId={userId} />
      <ProfileSection userId={userId} />
      <div style={{
        display: "grid",
        gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",
        gap: 32,
        alignItems: "start",
      }}>
        {/* Left column: action items */}
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          <PendingReviewsNudge />
          <UpcomingBookings userId={userId} />
        </div>

        {/* Right column: sauna management */}
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          <MySaunas userId={userId} />
        </div>

        {/* Full-width bottom: reviews */}
        <div style={{ gridColumn: narrow ? "auto" : "1 / -1" }}>
          <Reviews userId={userId} />
        </div>
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const termsBannerStyle: React.CSSProperties = {
  marginBottom: 32, padding: "18px 20px",
  background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8,
};
const pendingBannerStyle: React.CSSProperties = {
  marginBottom: 32, padding: "18px 20px",
  background: "#fffbeb", border: "2px solid #f59e0b", borderRadius: 8,
};
const pendingRowStyle: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12,
  padding: "12px 16px", marginBottom: 8, background: "#fff",
  border: "1px solid #fde68a", borderRadius: 6,
};
const profileEditBtn: React.CSSProperties = {
  fontSize: 13, color: "#2563eb", background: "none", border: "1px solid #2563eb",
  borderRadius: 6, padding: "4px 12px", cursor: "pointer",
};
const profileSaveBtn: React.CSSProperties = {
  padding: "10px 24px", background: "#2563EB", color: "#fff",
  border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
};
const profileCancelBtn: React.CSSProperties = {
  padding: "10px 24px", background: "#fff", color: "#374151",
  border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontSize: 14,
};
const emptyStyle: React.CSSProperties = { fontSize: 14, color: "#9ca3af", margin: "4px 0 0" };
const actionLink: React.CSSProperties = { fontSize: 14, color: "#2563EB", textDecoration: "none" };
const toggleBtn: React.CSSProperties = {
  fontSize: 12, padding: "4px 12px", border: "none", borderRadius: 12,
  color: "#fff", cursor: "pointer", whiteSpace: "nowrap",
};
const approveBtn: React.CSSProperties = {
  padding: "6px 14px", background: "#16a34a", color: "#fff",
  border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600,
};
const declineBtn: React.CSSProperties = {
  padding: "6px 14px", background: "#fff", color: "#ef4444",
  border: "1px solid #ef4444", borderRadius: 6, cursor: "pointer", fontSize: 13,
};
